import { useState, useCallback, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  useScheduler,
  slotToTime,
  timeToSlot,
  formatDateKey,
  generateTimeOptions,
} from "../hooks/useScheduler";
import type { SchedulerEvent } from "../hooks/useScheduler";
import { useMarkers } from "../hooks/useMarkers";
import SchedulerGrid from "./SchedulerGrid";
import MarkerList from "./MarkerList";
import SchedulerTimeline from "./SchedulerTimeline";
import Tabs from "@/shared/components/ui/Tabs";
import "./scheduler.css";

/* ── Time options (memoised once) ─────────────────────────────────── */

const TIME_OPTIONS = generateTimeOptions();

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
] as const;

function formatWeekRange(weekStart: Date): string {
  const end = new Date(weekStart);
  end.setDate(end.getDate() + 6);
  const s = `${weekStart.getDate()} ${MONTH_NAMES[weekStart.getMonth()]}`;
  const e = `${end.getDate()} ${MONTH_NAMES[end.getMonth()]} ${end.getFullYear()}`;
  return `${s} — ${e}`;
}

/* ── Sub-tabs ─────────────────────────────────────────────────────── */

const SUB_TABS = ["Calendar", "Markers", "Timeline"] as const;

export default function SchedulerManagement() {
  const sched = useScheduler();
  const [sp] = useSearchParams();
  const [subTab, setSubTab] = useState<string>("Calendar");
  const [miniMonth, setMiniMonth] = useState(
    () => new Date(sched.weekStart.getFullYear(), sched.weekStart.getMonth(), 1),
  );

  // Sync sub-tab with URL
  useEffect(() => {
    const v = sp.get("schTab");
    if (v && (SUB_TABS as readonly string[]).includes(v)) setSubTab(v);
  }, [sp]);

  const handleMiniDayClick = useCallback(
    (d: Date) => sched.setWeek(d),
    [sched.setWeek],
  );

  return (
    <div className="sch-root">
      {/* ── Left sidebar: mini calendar + controls ─────────── */}
      <aside className="sch-sidebar">
        {/* Lock toggle */}
        <button
          type="button"
          className={`sch-lock-btn${sched.locked ? " sch-lock-btn--active" : ""}`}
          onClick={sched.toggleLock}
        >
          <span className="sch-lock-icon">{sched.locked ? "\uD83D\uDD12" : "\uD83D\uDD13"}</span>
          {sched.locked ? "Calendar Locked" : "Lock Calendar"}
        </button>

        {/* Mini month calendar */}
        <MiniCalendar
          month={miniMonth}
          weekStart={sched.weekStart}
          onSelectDay={handleMiniDayClick}
          onNavPrev={() =>
            setMiniMonth(new Date(miniMonth.getFullYear(), miniMonth.getMonth() - 1, 1))
          }
          onNavNext={() =>
            setMiniMonth(new Date(miniMonth.getFullYear(), miniMonth.getMonth() + 1, 1))
          }
        />

        <button
          type="button"
          className="sch-today-btn"
          onClick={() => {
            sched.navToday();
            const now = new Date();
            setMiniMonth(new Date(now.getFullYear(), now.getMonth(), 1));
          }}
        >
          Today
        </button>

        <div className="sch-event-count">
          {sched.events.length} event{sched.events.length !== 1 ? "s" : ""} total
          {sched.loading && <span className="sch-loading-dot" />}
        </div>
      </aside>

      {/* ── Main area ──────────────────────────────────────── */}
      <div className="sch-main">
        {/* Sub-tabs */}
        <Tabs
          tabs={[...SUB_TABS]}
          activeTab={subTab}
          onTabChange={setSubTab}
          urlKey="schTab"
        />

        {subTab === "Calendar" && (
          <CalendarView sched={sched} />
        )}

        {subTab === "Markers" && (
          <MarkerList />
        )}

        {subTab === "Timeline" && (
          <SchedulerTimeline />
        )}
      </div>

      {/* ── Event editor dialog ────────────────────────────── */}
      {sched.editingEvent && (
        <EventEditor
          event={sched.editingEvent}
          locked={sched.locked}
          saving={sched.saving}
          onSave={sched.saveEvent}
          onDelete={sched.removeEvent}
          onClose={() => sched.setEditing(null)}
        />
      )}
    </div>
  );
}

/* ── Calendar View (grid + toolbar) ────────────────────────────────── */

function CalendarView({ sched }: { sched: ReturnType<typeof useScheduler> }) {
  const fromKey = formatDateKey(sched.weekStart);
  const toDate = new Date(sched.weekStart);
  toDate.setDate(toDate.getDate() + 6);
  const toKey = formatDateKey(toDate);
  const { markers } = useMarkers(fromKey, toKey);

  return (
    <>
      {/* Week navigation bar */}
      <div className="sch-toolbar">
        <button
          type="button"
          className="sch-nav-btn"
          onClick={sched.navPrevWeek}
          aria-label="Previous week"
        >
          &#x2039;
        </button>
        <span className="sch-toolbar__range">
          {formatWeekRange(sched.weekStart)}
        </span>
        <button
          type="button"
          className="sch-nav-btn"
          onClick={sched.navNextWeek}
          aria-label="Next week"
        >
          &#x203A;
        </button>
      </div>

      <SchedulerGrid
        weekDays={sched.weekDays}
        events={sched.events}
        markers={markers}
        locked={sched.locked}
        dragRange={sched.dragRange}
        eventsForDay={sched.eventsForDay}
        onDragStart={sched.dragStart}
        onDragMove={sched.dragMove}
        onDragEnd={sched.dragEnd}
        onDragCancel={sched.dragCancel}
        onClickEvent={(ev) => sched.setEditing(ev)}
      />
    </>
  );
}

/* ── Mini Calendar ─────────────────────────────────────────────────── */

function MiniCalendar({
  month,
  weekStart,
  onSelectDay,
  onNavPrev,
  onNavNext,
}: {
  month: Date;
  weekStart: Date;
  onSelectDay: (d: Date) => void;
  onNavPrev: () => void;
  onNavNext: () => void;
}) {
  const grid = buildMiniGrid(month);
  const today = new Date();
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 6);

  return (
    <div className="sch-mini">
      <div className="sch-mini__header">
        <button type="button" className="sch-mini__nav" onClick={onNavPrev}>
          &#x2039;
        </button>
        <span className="sch-mini__title">
          {MONTH_NAMES[month.getMonth()]} {month.getFullYear()}
        </span>
        <button type="button" className="sch-mini__nav" onClick={onNavNext}>
          &#x203A;
        </button>
      </div>

      <div className="sch-mini__weekdays">
        {["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"].map((wd) => (
          <span key={wd} className="sch-mini__wd">{wd}</span>
        ))}
      </div>

      <div className="sch-mini__grid">
        {grid.map((row, ri) =>
          row.map((day, ci) => {
            if (!day) {
              return <span key={`${ri}-${ci}`} className="sch-mini__day sch-mini__day--empty" />;
            }
            const isToday =
              day.getFullYear() === today.getFullYear() &&
              day.getMonth() === today.getMonth() &&
              day.getDate() === today.getDate();
            const inWeek = day >= weekStart && day <= weekEnd;

            return (
              <button
                key={`${ri}-${ci}`}
                type="button"
                className={[
                  "sch-mini__day",
                  isToday ? "sch-mini__day--today" : "",
                  inWeek ? "sch-mini__day--in-week" : "",
                ].filter(Boolean).join(" ")}
                onClick={() => onSelectDay(day)}
              >
                {day.getDate()}
              </button>
            );
          }),
        )}
      </div>
    </div>
  );
}

function buildMiniGrid(month: Date): (Date | null)[][] {
  const year = month.getFullYear();
  const m = month.getMonth();
  const firstDay = new Date(year, m, 1);
  const daysInMonth = new Date(year, m + 1, 0).getDate();
  const startOffset = (firstDay.getDay() + 6) % 7;

  const rows: (Date | null)[][] = [];
  let dayNum = 1 - startOffset;

  for (let r = 0; r < 6; r++) {
    const row: (Date | null)[] = [];
    for (let c = 0; c < 7; c++, dayNum++) {
      if (dayNum < 1 || dayNum > daysInMonth) row.push(null);
      else row.push(new Date(year, m, dayNum));
    }
    rows.push(row);
  }
  return rows;
}

/* ── Event Editor (15-min dropdown time pickers) ──────────────────── */

function EventEditor({
  event,
  locked,
  saving,
  onSave,
  onDelete,
  onClose,
}: {
  event: SchedulerEvent;
  locked: boolean;
  saving: boolean;
  onSave: (ev: SchedulerEvent) => void;
  onDelete: (id: string) => void;
  onClose: () => void;
}) {
  const [title, setTitle] = useState(event.title);
  const [date, setDate] = useState(event.date);
  const [startTime, setStartTime] = useState(event.start_time);
  const [endTime, setEndTime] = useState(event.end_time);
  const [notes, setNotes] = useState(event.notes || "");
  const [recurring, setRecurring] = useState(event.recurring || "none");
  const isExisting = !!event.id;
  const isRecurringInstance = !!event.recurring_source;

  // Compute validation
  const startSlot = timeToSlot(startTime);
  const endSlot = timeToSlot(endTime);
  const isValid = title.trim().length > 0 && endSlot > startSlot && /^\d{4}-\d{2}-\d{2}$/.test(date);

  const handleSave = useCallback(() => {
    if (!isValid || locked) return;
    onSave({
      ...event,
      // For recurring instances, save against the source event
      id: isRecurringInstance ? event.recurring_source! : event.id,
      title: title.trim(),
      date,
      start_time: startTime,
      end_time: endTime,
      start_slot: startSlot,
      end_slot: endSlot,
      notes,
      recurring: recurring as SchedulerEvent["recurring"],
    });
  }, [event, title, date, startTime, endTime, startSlot, endSlot, notes, recurring, isValid, locked, isRecurringInstance, onSave]);

  // Filter end times to only show options after start time
  const endOptions = useMemo(
    () => TIME_OPTIONS.filter((o) => timeToSlot(o.value) > startSlot),
    [startSlot],
  );

  return (
    <div className="sch-editor-overlay" onClick={onClose}>
      <div className="sch-editor" onClick={(e) => e.stopPropagation()}>
        <div className="sch-editor__header">
          <span className="sch-editor__badge" style={{ background: event.color }} />
          <span className="sch-editor__title-label">
            {isExisting ? "Edit Event" : "New Event"}
          </span>
        </div>

        {/* Title */}
        <input
          className="sch-editor__input"
          placeholder="Event title..."
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          autoFocus
          disabled={locked}
          onKeyDown={(e) => {
            if (e.key === "Enter" && isValid) handleSave();
            if (e.key === "Escape") onClose();
          }}
        />

        {/* Date */}
        <div className="sch-editor__row">
          <label className="sch-editor__label">Date</label>
          <input
            type="date"
            className="sch-editor__input sch-editor__date-input"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            disabled={locked}
          />
        </div>

        {/* Time pickers — 15 min resolution dropdowns */}
        <div className="sch-editor__row sch-editor__time-row">
          <div className="sch-editor__time-field">
            <label className="sch-editor__label">Start</label>
            <select
              className="sch-editor__select"
              value={startTime}
              onChange={(e) => {
                setStartTime(e.target.value);
                // Auto-advance end if needed
                const newStart = timeToSlot(e.target.value);
                if (timeToSlot(endTime) <= newStart) {
                  setEndTime(slotToTime(Math.min(newStart + 4, 95))); // default +1h
                }
              }}
              disabled={locked}
            >
              {TIME_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          <span className="sch-editor__time-sep">—</span>

          <div className="sch-editor__time-field">
            <label className="sch-editor__label">End</label>
            <select
              className="sch-editor__select"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              disabled={locked}
            >
              {endOptions.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Recurring */}
        <div className="sch-editor__row">
          <label className="sch-editor__label">Recurring</label>
          <select
            className="sch-editor__select"
            value={recurring}
            onChange={(e) => setRecurring(e.target.value as SchedulerEvent['recurring'])}
            disabled={locked || isRecurringInstance}
          >
            <option value="none">None</option>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>
        </div>

        {isRecurringInstance && (
          <div className="sch-editor__info">
            This is a recurring instance. Edits apply to the source event.
          </div>
        )}

        {/* Notes */}
        <textarea
          className="sch-editor__input sch-editor__notes"
          placeholder="Notes (optional)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          disabled={locked}
        />

        {/* Validation hint */}
        {!isValid && title.trim().length > 0 && endSlot <= startSlot && (
          <div className="sch-editor__error">End time must be after start time</div>
        )}

        {/* Actions */}
        <div className="sch-editor__actions">
          {!locked && (
            <button
              type="button"
              className="sch-editor__save"
              disabled={!isValid || saving}
              onClick={handleSave}
            >
              {saving ? "Saving..." : isExisting ? "Update" : "Save"}
            </button>
          )}
          {isExisting && !locked && (
            <button
              type="button"
              className="sch-editor__delete"
              disabled={saving}
              onClick={() => onDelete(event.id)}
            >
              Delete
            </button>
          )}
          <button type="button" className="sch-editor__cancel" onClick={onClose}>
            {locked ? "Close" : "Cancel"}
          </button>
        </div>
      </div>
    </div>
  );
}
